import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Menu, X, ChevronDown, Phone, Mail, Instagram, Facebook } from "lucide-react";
import logoAsset from "@/assets/bosque-logo.png.asset.json";
const logoMark = logoAsset.url;
import { useT, useMedia } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/site/LanguageSwitcher";
import siteConfig from "@/config/site.json";
import navigation from "@/config/navigation.json";

type NavChild = {
  to: string;
  labelKey: string;
  labelDefault: string;
  descKey?: string;
  descDefault?: string;
  badgeKey?: string;
  badgeDefault?: string;
};

type NavLink = {
  to: string;
  labelKey: string;
  labelDefault: string;
  children?: NavChild[];
};

const WA_GENERAL = "https://api.whatsapp.com/send?phone=34666654094";
const WA_ESCOLARES =
  "https://api.whatsapp.com/send?phone=34615176058&text=Hola%2C%20tengo%20tu%20informaci%C3%B3n%20de%20WhatsApp%20de%20tu%20web.";
const RESERVAS_EMAIL = "reservas@bosquesuspendido.com";

function WhatsAppGlyph({ size = 14 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export function Nav() {
  const [open, setOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [waOpen, setWaOpen] = useState(false);
  const { tk } = useT();
  const logo = useMedia("brand.logo.mark", logoMark, siteConfig.brand.name);

  const links: NavLink[] = navigation.primary as NavLink[];
  const cta = {
    to: navigation.cta.to as "/contact",
    label: tk(navigation.cta.labelKey, navigation.cta.labelDefault),
  };
  const phone = siteConfig.contact.phones[0];

  return (
    <header className="sticky top-0 z-50">
      {/* Amber announcement / utility bar */}
      <div className="bg-gradient-to-b from-gold to-gold/90 text-accent-foreground border-b border-accent-foreground/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-1.5 flex items-center justify-between gap-3">
          {/* Left: WhatsApp dropdown + email */}
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <div className="relative">
              <button
                type="button"
                onClick={() => setWaOpen((v) => !v)}
                aria-expanded={waOpen}
                aria-haspopup="menu"
                className="flex items-center gap-1.5 rounded-full bg-accent-foreground/10 hover:bg-accent-foreground/20 px-3 py-1.5 text-[11px] tracking-[0.1em] uppercase font-bold transition-colors"
              >
                <WhatsAppGlyph size={14} />
                <span>WhatsApp</span>
                <ChevronDown size={12} className={`transition-transform ${waOpen ? "rotate-180" : ""}`} />
              </button>
              {waOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setWaOpen(false)} aria-hidden />
                  <div
                    role="menu"
                    className="absolute left-0 top-full mt-2 z-50 w-60 rounded-xl border border-primary-foreground/15 bg-primary text-primary-foreground shadow-2xl shadow-black/40 p-2"
                  >
                    <a
                      href={WA_GENERAL}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="menuitem"
                      onClick={() => setWaOpen(false)}
                      className="block rounded-lg px-4 py-3 hover:bg-primary-foreground/10 transition-colors"
                    >
                      <div className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-bold">
                        <WhatsAppGlyph size={14} />
                        {tk("nav.whatsapp.general", "General")}
                      </div>
                      <div className="text-xs text-primary-foreground/70 mt-1 leading-relaxed">
                        {tk("nav.whatsapp.general.desc", "Reservas e información")}
                      </div>
                    </a>
                    <a
                      href={WA_ESCOLARES}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="menuitem"
                      onClick={() => setWaOpen(false)}
                      className="block rounded-lg px-4 py-3 hover:bg-primary-foreground/10 transition-colors"
                    >
                      <div className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-bold">
                        <WhatsAppGlyph size={14} />
                        {tk("nav.whatsapp.escolares", "Escolares")}
                      </div>
                      <div className="text-xs text-primary-foreground/70 mt-1 leading-relaxed">
                        {tk("nav.whatsapp.escolares.desc", "Grupos y centros educativos")}
                      </div>
                    </a>
                  </div>
                </>
              )}
            </div>
            {phone && (
              <a
                href={`tel:${phone.number.replace(/\s/g, "")}`}
                className="hidden sm:flex items-center gap-1.5 text-xs tracking-[0.1em] font-bold hover:opacity-75 transition-opacity"
              >
                <Phone size={13} className="shrink-0" />
                <span>{phone.number}</span>
              </a>
            )}
          </div>

          {/* Right: phone + socials */}
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <a
              href={`mailto:${RESERVAS_EMAIL}`}
              className="flex items-center gap-1.5 text-xs font-bold hover:opacity-75 transition-opacity truncate"
            >
              <Mail size={13} className="shrink-0" />
              <span className="truncate">{RESERVAS_EMAIL}</span>
            </a>
            <span className="hidden sm:block w-px h-4 bg-accent-foreground/20" aria-hidden />
            <div className="hidden sm:flex items-center gap-2">
              {siteConfig.social.facebook && (
                <a
                  href={siteConfig.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="w-7 h-7 rounded-full bg-accent-foreground/10 hover:bg-accent-foreground/25 flex items-center justify-center transition-colors"
                >
                  <Facebook size={13} />
                </a>
              )}
              {siteConfig.social.instagram && (
                <a
                  href={siteConfig.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-7 h-7 rounded-full bg-accent-foreground/10 hover:bg-accent-foreground/25 flex items-center justify-center transition-colors"
                >
                  <Instagram size={13} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Deep forest-green menu bar */}
      <div className="bg-primary text-primary-foreground shadow-lg shadow-black/20">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="relative flex items-center h-7 md:h-8 w-40 lg:w-52 shrink-0" aria-label={siteConfig.brand.name}>
            <span
              aria-hidden
              className="absolute -left-2 -top-2 w-24 h-24 md:w-32 md:h-32 rounded-full bg-primary shadow-lg shadow-black/30"
            />
            <img
              src={logo.url}
              sizes="(max-width: 768px) 200px, 280px"
              alt={logo.alt}
              width={280}
              height={120}
              className="absolute left-0 top-0 h-20 md:h-28 w-auto max-w-none drop-shadow-md"
              loading="eager"
              decoding="async"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-5 lg:gap-8 whitespace-nowrap">
            {links.map((l) => {
              const label = tk(l.labelKey, l.labelDefault);
              if (!l.children || l.children.length === 0) {
                return (
                  <Link
                    key={l.to}
                    to={l.to as "/"}
                    className="text-xs tracking-[0.2em] uppercase font-bold text-primary-foreground/85 hover:text-gold-soft transition-colors"
                    activeProps={{ className: "text-gold-soft" }}
                  >
                    {label}
                  </Link>
                );
              }
              return (
                <div key={l.to} className="relative group">
                  <Link
                    to={l.to as "/"}
                    className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase font-bold text-primary-foreground/85 hover:text-gold-soft transition-colors group-hover:text-gold-soft"
                    activeProps={{ className: "text-gold-soft" }}
                  >
                    {label}
                    <ChevronDown size={12} className="opacity-70 transition-transform group-hover:rotate-180" />
                  </Link>
                  <div
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50"
                    role="menu"
                  >
                    <div className="w-[320px] rounded-xl border border-primary-foreground/15 bg-primary shadow-2xl shadow-black/40 p-2">
                      {l.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to as "/"}
                          className="block rounded-lg px-4 py-3 hover:bg-primary-foreground/10 transition-colors"
                          activeProps={{ className: "bg-primary-foreground/10" }}
                        >
                          <div className="flex items-center gap-2">
                            <div className="text-xs tracking-[0.2em] uppercase font-bold text-primary-foreground">
                              {tk(c.labelKey, c.labelDefault)}
                            </div>
                            {c.badgeDefault && (
                              <span className="text-[9px] tracking-[0.2em] uppercase bg-gold text-accent-foreground rounded-full px-2 py-0.5 font-bold">
                                {tk(c.badgeKey ?? c.labelKey + ".badge", c.badgeDefault)}
                              </span>
                            )}
                          </div>
                          {c.descDefault && (
                            <div className="text-xs text-primary-foreground/70 mt-1.5 normal-case tracking-normal leading-relaxed">
                              {tk(c.descKey ?? c.labelKey + ".desc", c.descDefault)}
                            </div>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <Link
              to={cta.to}
              className="inline-flex whitespace-nowrap wood-btn text-xs tracking-[0.2em] uppercase font-bold px-5 lg:px-6 py-2.5"
            >
              {cta.label}
            </Link>
            <LanguageSwitcher />
          </div>

          <div className="flex md:hidden items-center gap-3">
            <button aria-label="Menu" className="text-primary-foreground" onClick={() => setOpen((v) => !v)}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-primary-foreground/15">
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => {
                const label = tk(l.labelKey, l.labelDefault);
                if (!l.children || l.children.length === 0) {
                  return (
                    <Link
                      key={l.to}
                      to={l.to as "/"}
                      onClick={() => setOpen(false)}
                      className="block py-3 text-sm tracking-[0.2em] uppercase font-bold text-primary-foreground/85 hover:text-gold-soft border-b border-primary-foreground/10"
                    >
                      {label}
                    </Link>
                  );
                }
                const expanded = mobileExpanded === l.to;
                return (
                  <div key={l.to} className="border-b border-primary-foreground/10">
                    <div className="flex items-center justify-between py-3">
                      <Link
                        to={l.to as "/"}
                        onClick={() => setOpen(false)}
                        className="text-sm tracking-[0.2em] uppercase font-bold text-primary-foreground/85 hover:text-gold-soft"
                      >
                        {label}
                      </Link>
                      <button
                        type="button"
                        aria-label="Toggle submenu"
                        aria-expanded={expanded}
                        onClick={() => setMobileExpanded(expanded ? null : l.to)}
                        className="p-2 -mr-2 text-primary-foreground/70"
                      >
                        <ChevronDown
                          size={16}
                          className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                        />
                      </button>
                    </div>
                    {expanded && (
                      <ul className="pb-3 pl-4 flex flex-col gap-1">
                        {l.children.map((c) => (
                          <li key={c.to}>
                            <Link
                              to={c.to as "/"}
                              onClick={() => setOpen(false)}
                              className="block py-2 text-xs tracking-[0.2em] uppercase font-semibold text-primary-foreground/70 hover:text-gold-soft"
                            >
                              {tk(c.labelKey, c.labelDefault)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
              <div className="mt-5 flex items-center justify-between gap-4">
                <Link
                  to={cta.to}
                  onClick={() => setOpen(false)}
                  className="inline-flex wood-btn text-xs tracking-[0.2em] uppercase font-bold px-5 py-2.5"
                >
                  {cta.label}
                </Link>
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
